import './ChatbotConfig.scss';

import { Badge, Form } from 'react-bootstrap';
import React, { useEffect, useRef, useState } from 'react';

import AppButton from '../../commons/buttons/AppButton';
import AppLinkButton from '../../commons/buttons/AppLinkButton';
import AppLoading from '../../commons/AppLoading';
import FloatingVideoButton from '../../commons/buttons/FloatingVideoButton';
import HallAppToast from '../../commons/HallAppToast';
import { QRCodeCanvas } from 'qrcode.react';
import SpaceMenuBottom from '../../commons/SpaceMenuBottom';
import WhatsappAPI from './WhatsappAPI';

const whatsappAPI = new WhatsappAPI();

const POLL_MS = 3000;
const QR_EXPIRES_MS = 120000;

const ChatbotConfig = () => {
  const [qrCode, setQrCode] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [qrGeneratedAt, setQrGeneratedAt] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Configurações
  const [confirmMsgEnabled, setConfirmMsgEnabled] = useState(false);
  const [confirmMsgDaysBefore, setConfirmMsgDaysBefore] = useState(2);

  const [reminderMsgEnabled, setReminderMsgEnabled] = useState(false);
  const [reminderMsgDaysBefore, setReminderMsgDaysBefore] = useState(1);

  const [birthdayMsgEnabled, setBirthdayMsgEnabled] = useState(false);
  const [feedbackMsgEnabled, setFeedbackMsgEnabled] = useState(false);

  const pollRef = useRef(null);
  const expireTimerRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const clearExpireTimer = () => {
    if (expireTimerRef.current) clearTimeout(expireTimerRef.current);
  };

  const getStatus = async () => {
    try {
      const resp = await whatsappAPI.getStatus();
      const connected = !!resp?.data?.isConnected;
      setIsConnected(connected);

      if (connected) {
        setQrCode(null);
        setErrorMsg('');
        stopPolling();
        clearExpireTimer();
      }
    } catch {
      HallAppToast.genericError();
    }
  };

  const connect = async () => {
    try {
      setIsLoading(true);
      const resp = await whatsappAPI.getQrCodeToConnect();
      const code = resp?.data?.qrcode?.code;

      setQrCode(code);
      setQrGeneratedAt(Date.now());

      pollRef.current = setInterval(getStatus, POLL_MS);

      expireTimerRef.current = setTimeout(() => {
        setQrCode(null);
        setErrorMsg('QR Code expirou');
      }, QR_EXPIRES_MS);
    } catch {
      HallAppToast.error('Erro ao conectar');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    await whatsappAPI.logout();
    setIsConnected(false);
  };

  useEffect(() => {
    getStatus();
    getCompanyData();
  }, []);

  const getCompanyData = async () => {
    try {
      const resp = await whatsappAPI.getBotConfig();
      const config = resp.data;

      setConfirmMsgEnabled(config?.schedulerNotification ?? false);
      setConfirmMsgDaysBefore(config?.schedulerNotificationDaysBefore ?? 2);

      setReminderMsgEnabled(config?.schedulerPreReminderEnabled ?? false);
      setReminderMsgDaysBefore(config?.schedulerPreReminderDaysBefore ?? 1);

      setBirthdayMsgEnabled(config?.birthdayNotification ?? false);
      setFeedbackMsgEnabled(config?.feedbackNotification ?? false);

    } catch {
      HallAppToast.genericError();
    }
  };

  const saveConfig = async () => {
    try {
      setIsLoading(true);

      const data = {
        schedulerNotification: confirmMsgEnabled,
        schedulerNotificationDaysBefore: confirmMsgDaysBefore,

        schedulerPreReminderEnabled: reminderMsgEnabled,
        schedulerPreReminderDaysBefore: reminderMsgDaysBefore,

        birthdayNotification: birthdayMsgEnabled,
        feedbackNotification: feedbackMsgEnabled,
      };

      await whatsappAPI.saveBotConfig(data);
      HallAppToast.success('Salvo com sucesso!');
    } catch {
      HallAppToast.error('Erro ao salvar');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="wa-config">
      <div className="wa-card">

        <header className="wa-header">
          <h2>WhatsApp</h2>
          <Badge bg={isConnected ? 'success' : 'warning'}>
            {isConnected ? 'Conectado' : 'Desconectado'}
          </Badge>
        </header>

        {!isConnected && !qrCode && (
          <AppButton name="Conectar WhatsApp" onClick={connect} />
        )}

        {qrCode && (
          <div>
            <QRCodeCanvas value={qrCode} />
          </div>
        )}

        <div className="wa-config-options">

          {/* 🔥 REMINDER */}
          <Form.Group>
            <Form.Check
              type="switch"
              label="Enviar lembrete antes do agendamento"
              checked={reminderMsgEnabled}
              onChange={(e) => setReminderMsgEnabled(e.target.checked)}
              disabled={!isConnected}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Dias antes (lembrete)</Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={reminderMsgDaysBefore}
              onChange={(e) => setReminderMsgDaysBefore(Number(e.target.value))}
              disabled={!reminderMsgEnabled}
            />
          </Form.Group>

          {/* 🔥 CONFIRMAÇÃO */}
          <Form.Group>
            <Form.Check
              type="switch"
              label="Enviar confirmação"
              checked={confirmMsgEnabled}
              onChange={(e) => setConfirmMsgEnabled(e.target.checked)}
              disabled={!isConnected}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Dias antes (confirmação)</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={confirmMsgDaysBefore}
              onChange={(e) => setConfirmMsgDaysBefore(Number(e.target.value))}
              disabled={!confirmMsgEnabled}
            />
          </Form.Group>

          {/* OUTROS */}
          <Form.Check
            type="switch"
            label="Mensagem de aniversário"
            checked={birthdayMsgEnabled}
            onChange={(e) => setBirthdayMsgEnabled(e.target.checked)}
          />

          <Form.Check
            type="switch"
            label="Solicitar feedback"
            checked={feedbackMsgEnabled}
            onChange={(e) => setFeedbackMsgEnabled(e.target.checked)}
          />

        </div>

        <hr></hr>

        {
          isLoading ? <AppLoading/> : <AppButton name="Salvar" onClick={saveConfig} />
        }
        <div style={{marginTop: '30px'}}>
          <AppButton name="Desconectar" onClick={logout} variant="danger" />
        </div>
        <SpaceMenuBottom />
      </div>
    </div>
  );
};

export default ChatbotConfig;

